import { Injectable } from '@angular/core';
import { jwtDecode } from 'jwt-decode';

export interface JwtPayload {
    [key: string]: any;
}

@Injectable({
    providedIn: 'root'
})
export class JwtService {

    constructor() { }

    /**
     * Safely decode a JWT token using jwt-decode library
     * @param token The JWT token to decode
     * @returns The decoded payload or null if invalid
     */
    decodeToken(token: string): JwtPayload | null {
        try {
            if (!token || typeof token !== 'string') {
                return null;
            }

            // Use jwt-decode library which handles all JWT token formats safely
            return jwtDecode<JwtPayload>(token);
        } catch (error) {
            console.error('Error decoding JWT token:', error);
            return null;
        }
    }

    /**
     * Get a specific claim from a JWT token
     * @param token The JWT token
     * @param claim The claim key to extract
     * @returns The claim value or null if not found
     */
    getClaim(token: string, claim: string): any {
        const payload = this.decodeToken(token);
        return payload ? payload[claim] : null;
    }

    /**
     * Get the user ID from a JWT token
     * @param token The JWT token
     * @returns The user ID or null if not found
     */
    getUserId(token: string): string | null {
        return this.getClaim(token, 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier');
    }

    /**
     * Get the tenant ID from a JWT token
     * @param token The JWT token
     * @returns The tenant ID or null if not found
     */
    getTenantId(token: string): string | null {
        return this.getClaim(token, 'tenant');
    }

    /**
     * Check if a JWT token is expired
     * @param token The JWT token
     * @returns True if expired, false otherwise
     */
    isTokenExpired(token: string): boolean {
        try {
            const payload = this.decodeToken(token);
            if (!payload || !payload.exp) {
                return true;
            }

            const currentTime = Math.floor(Date.now() / 1000);
            return payload.exp < currentTime;
        } catch (error) {
            console.error('Error checking token expiration:', error);
            return true;
        }
    }

    /**
     * Get token expiration time
     * @param token The JWT token
     * @returns Expiration timestamp or null if not found
     */
    getTokenExpiration(token: string): number | null {
        const payload = this.decodeToken(token);
        return payload?.exp || null;
    }
}
